import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    TextField,
    Typography,
    MenuItem,
    Checkbox,
    FormControlLabel,
    IconButton,
    Stack,
    Button
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import useTextField from '../../../../utility/hooks/useTextField';
import {validateFieldLength} from '../../../../utility/validationUtil';
import {useGetFormCategoriesQuery} from '../../../../api/services/formCategoriesService';
import PropTypes from 'prop-types';
import {availableComponents} from '../FormBuilder/FormBuilder';
import {determinePossibleOperations} from '../FormBuilder/ShowFieldWhen';
import AdmicityTabs from '../../../../shared-components/AdmicityTabs';

const CreateGroupFromResponsesDialog = ({
                                            open,
                                            title,
                                            onClose,
                                            onCreateGroup,
                                            onExtendGroup,
                                            schema,
                                            lookups,
                                            groupFilters,
                                            groups,
                                            membersType
                                        }) => {
    const nameField = useTextField({
        initialValue: '',
        validate: (value) => validateFieldLength(value, 256),
    });
    const {data: formCategories} = useGetFormCategoriesQuery();
    const [parentGroupFilterId, setParentGroupFilterId] = useState('');
    const [existingGroupFilterId, setExistingGroupFilterId] = useState('');
    const [selectedGroupLevel, setSelectedGroupLevel] = useState(null);
    const [parentGroupLevel, setParentGroupLevel] = useState(null);
    const [isGroupDynamic, setIsGroupDynamic] = useState(true);
    const [myGroup, setMyGroup] = useState(false);
    const [formCategoryId, setFormCategoryId] = useState(null);
    const [selectedQuestionAnswers, setSelectedQuestionAnswers] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const filteredGroupFilters = groupFilters?.modules
        .map(module => ({
            ...module,
            filters: module.filters.filter(filter => filter.membersType === membersType)
        })) || [];
    const groupFilterIds = groups?.map(group => group.groupFilterId) || [];

    const findGroupLevel = (groupId) => {
        if (!groupFilters?.modules) return null;

        for (const module of groupFilters.modules) {
            for (const filter of module.filters) {
                if (filter.id === groupId) {
                    return filter.level;
                }
            }
        }
        return null;
    };

    const resetState = () => {
        nameField.reset();
        setParentGroupFilterId('');
        setExistingGroupFilterId('');
        setSelectedGroupLevel(null);
        setParentGroupLevel(null);
        setMyGroup(false);
        setFormCategoryId(null);
        setIsGroupDynamic(true);
        setSelectedQuestionAnswers([]);
    };

    useEffect(() => {
        if (!open) {
            resetState();
            setActiveTab(0);
        }
    }, [open]);

    const handleCategoryChange = (e) => {
        const selectedCategoryId = e.target.value || null;
        setFormCategoryId(selectedCategoryId);
    };

    const handleSave = async () => {
        const questionAnswers = selectedQuestionAnswers
            .filter(({component, targetState}) => component && targetState != null)
            .map(({component, targetState}) => ({
                relatedComponentId: component.lookupType ? `${component.id}.id` : component.id,
                targetStateId: targetState
            }));

        if (activeTab === 0) {
            await onCreateGroup({
                parentGroupFilterId,
                groupName: nameField.value,
                myGroup,
                formCategoryId,
                isDynamic: isGroupDynamic,
                questionAnswers
            });
        } else {
            await onExtendGroup({
                existingGroupFilterId,
                questionAnswers
            });
        }
    };

    const addQuestionAnswer = () => {
        setSelectedQuestionAnswers(prev => [...prev, {component: null, targetState: null}]);
    };

    const updateQuestionAnswer = (index, field, value) => {
        setSelectedQuestionAnswers(prev => {
            const next = [...prev];
            next[index] = {...next[index], [field]: value};
            if (field === 'component') {
                next[index].targetState = null;
            }
            return next;
        });
    };

    const removeQuestionAnswer = (index) => {
        setSelectedQuestionAnswers(prev => prev.filter((_, i) => i !== index));
    };

    /** Once a row is added, both question and answer must be selected. */
    const isEveryQuestionAnswerComplete = selectedQuestionAnswers.length === 0 ||
        selectedQuestionAnswers.every(({component, targetState}) => component && targetState != null);

    const renderQuestionSelectComponents = () => {
        const selectableSchema = schema?.filter(option => availableComponents.includes(option.type)) || [];

        return (
            <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                    Optionally filter which responses are exported by question and answer. Leave empty to export all
                    responses.
                </Typography>
                <Stack spacing={2}>
                    {selectedQuestionAnswers.map((entry, index) => (
                        <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                                select
                                size="small"
                                sx={{flex: 1, minWidth: 0}}
                                value={entry.component || ''}
                                disabled={!schema}
                                label="Question"
                                onChange={e => updateQuestionAnswer(index, 'component', e.target.value || null)}
                            >
                                <MenuItem value="">
                                    <em>Select question</em>
                                </MenuItem>
                                {selectableSchema.length > 0
                                    ? selectableSchema.map(option => (
                                        <MenuItem key={option.id} value={option} sx={{maxWidth: '550px'}}>
                                            <div
                                                title={`${option.label} - ${option.description}`}
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {option.label} - {option.description}
                                            </div>
                                        </MenuItem>
                                    ))
                                    : <MenuItem disabled>No options available</MenuItem>}
                            </TextField>
                            <TextField
                                select
                                size="small"
                                sx={{flex: 1, minWidth: 0}}
                                value={entry.targetState ?? ''}
                                label="Equals"
                                disabled={!entry.component}
                                onChange={e => updateQuestionAnswer(index, 'targetState', e.target.value)}
                            >
                                {entry.component &&
                                    determinePossibleOperations(
                                        entry.component.lookupType
                                            ? {
                                                ...entry.component,
                                                options: lookups[entry.component.lookupType] || []
                                            }
                                            : entry.component,
                                        {checkboxOnlyChecked: true}
                                    )?.map(operation => (
                                        <MenuItem
                                            key={operation.value}
                                            value={operation.value}
                                            sx={{maxWidth: '550px'}}
                                        >
                                            <div
                                                title={operation.title}
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {operation.title}
                                            </div>
                                        </MenuItem>
                                    ))}
                            </TextField>
                            <IconButton
                                size="small"
                                onClick={() => removeQuestionAnswer(index)}
                                aria-label="Remove question"
                                sx={{mt: 0.5}}
                            >
                                <DeleteOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </Stack>
                    ))}
                    <Box>
                        <Button
                            size="small"
                            startIcon={<AddIcon/>}
                            onClick={addQuestionAnswer}
                            disabled={!schema}
                        >
                            Add question filter
                        </Button>
                    </Box>
                </Stack>
            </Box>
        );
    };

    const tabs = [
        {
            label: 'Export to New Group',
            component: (
                <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    <TextField
                        required
                        fullWidth
                        label="Group Name"
                        variant="outlined"
                        {...nameField}
                    />
                    <TextField
                        select
                        value={formCategoryId || ''}
                        label="Category"
                        helperText="Please select a category or clear the selection"
                        fullWidth
                        onChange={handleCategoryChange}
                        SelectProps={{
                            renderValue: (selected) => {
                                if (!selected) return 'No category selected';
                                const selectedCategory = formCategories.find(
                                    (category) => category.id === selected
                                );
                                if (!selectedCategory) return 'Invalid category selected';
                                return (
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: selectedCategory.color || 'grey',
                                                marginRight: 1,
                                            }}
                                        />
                                        {selectedCategory.name || ''}
                                    </Box>
                                );
                            },
                        }}
                    >
                        <MenuItem value="">
                            <Box display="flex" alignItems="center" sx={{color: 'grey'}}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: 'transparent',
                                        marginRight: 1,
                                    }}
                                />
                                No category
                            </Box>
                        </MenuItem>
                        {formCategories?.length > 0 &&
                            formCategories.map(({id, name, color}) => (
                                <MenuItem key={id} value={id}>
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: color || 'grey',
                                                marginRight: 1,
                                            }}
                                        />
                                        {name}
                                    </Box>
                                </MenuItem>
                            ))}
                    </TextField>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Parent Group
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Choose if you want to add this as a sub-group
                        </Typography>
                        <AdmicityGroupFilterSelect
                            sx={{marginTop: 1, width: '100%'}}
                            data={filteredGroupFilters}
                            value={parentGroupFilterId}
                            onChange={(nodeId, paths) => {
                                setParentGroupFilterId(nodeId, paths);
                                setParentGroupLevel(findGroupLevel(nodeId));
                            }}
                            isRequired={false}
                        />
                        {parentGroupLevel && parentGroupLevel > 2 && (
                            <Alert severity="warning" sx={{mt: 1}}>
                                Only profiles that exist in the parent group will be exported to this subgroup.
                            </Alert>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isGroupDynamic}
                                    onChange={(e) => setIsGroupDynamic(e.target.checked)}/>
                            }
                            label="Dynamic Group (auto-includes students)"
                        />
                    </Box>
                    {renderQuestionSelectComponents()}
                </Box>
            ),
            isValid: () => nameField.value && !nameField.error && isEveryQuestionAnswerComplete
        },
        {
            label: 'Export to Existing Group',
            component: (
                <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    <AdmicityGroupFilterSelect
                        sx={{width: '100%'}}
                        data={groupFilters?.modules
                            .map(module => ({
                                ...module,
                                filters: module.filters.filter(filter => filter.membersType === membersType)
                            }))
                        }
                        value={existingGroupFilterId}
                        onChange={(nodeId) => {
                            setExistingGroupFilterId(nodeId);
                            setSelectedGroupLevel(findGroupLevel(nodeId));
                        }}
                        isRequired={false}
                        label={'Existing group'}
                    />
                    {groupFilterIds.includes(existingGroupFilterId) && (
                        <Alert severity="error" sx={{mt: 1}}>
                            This group is already linked to this form. Please select a different group.
                        </Alert>
                    )}
                    {selectedGroupLevel && selectedGroupLevel > 2 && (
                        <Alert severity="warning" sx={{mt: 1}}>
                            Only profiles that exist in the parent group will be exported to this subgroup.
                        </Alert>
                    )}
                    {renderQuestionSelectComponents()}
                </Box>
            ),
            isValid: () => {
                const isGroupAlreadyLinked = groupFilterIds.includes(existingGroupFilterId);
                return !!existingGroupFilterId && !isGroupAlreadyLinked && isEveryQuestionAnswerComplete;
            }
        }
    ];

    return (
        <AdmicityDialog
            handleClose={onClose}
            title={title}
            open={open}
            actions={[
                {
                    label: 'Cancel',
                    onClick: onClose,
                },
                {
                    label: 'Save',
                    disabled: !tabs[activeTab]?.isValid(),
                    onClick: handleSave,
                },
            ]}
        >
            <Alert severity="info">
                Please note that students will be assigned to the new group based on the filters applied to the form
                responses
            </Alert>
            <AdmicityTabs
                tabs={tabs}
                variant="fullWidth"
                tabIndex={activeTab}
                onChange={(tabIndex) => {
                    resetState();
                    setActiveTab(tabIndex);
                }}
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </AdmicityDialog>
    );
};

CreateGroupFromResponsesDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateGroup: PropTypes.func.isRequired,
    onExtendGroup: PropTypes.func.isRequired,
    schema: PropTypes.object.isRequired,
    lookups: PropTypes.object,
    groupFilters: PropTypes.object,
    groups: PropTypes.array,
    membersType: PropTypes.number
};

export default CreateGroupFromResponsesDialog;
